import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import H from '@here/maps-api-for-javascript';
import { useLocations } from '@/api/Locations';
import { LinkHealth, OperatorPresence, VesselPositions } from '@/types';
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
  HERE_MAP_KEY,
  PRESENCE_COLORS,
} from './constants';
import { toGroundPoint } from './toGroundPoint';

export type OperatorMapMarker = {
  lat: number;
  lng: number;
  operatorId: number;
  presence: OperatorPresence;
  linkHealth?: LinkHealth | null;
  label?: string;
};

export interface MapFullscreenData {
  zoom: number;
  bounds: H.geo.Rect;
}

export type MapHandle = {
  takeSnapshot: () => Promise<string | null>;
  getView: () => MapFullscreenData | null;
};

type MapProps = {
  markers?: H.geo.IPoint[];
  operatorMarkers?: OperatorMapMarker[];
  onOperatorMarkerClick?: (operatorId: number) => void;
  icon?: string;
  circle?: { center: H.geo.IPoint; radius?: number };
  center?: H.geo.IPoint;
  /** When set (and `center` is not), map centers on this location's lat/lng. */
  siteId?: number;
  zoom?: number;
  kmlUrl?: string;
  vessels?: VesselPositions[];
  shouldReloadKml?: boolean;
  onReloadKml?: () => void;
  showBorder?: boolean;
  showMapSettings?: boolean;
  /** When true, fetch locations and render mapImage overlays (Command Centre / admin only). */
  showSiteOverlays?: boolean;
  /** Switch HERE base layer between vector streets and satellite. */
  satelliteView?: boolean;
  /** Attach layers to this single HERE instance; null means it is being released. */
  onMapReady?: (map: H.Map | null) => void;
  suppressLegacyLayers?: boolean;
};

interface MapOverlay {
  path: string;
  bounds: {
    north: number;
    east: number;
    south: number;
    west: number;
  };
}

export const Map = forwardRef<MapHandle, MapProps>(function Map(
  {
    markers,
    operatorMarkers,
    onOperatorMarkerClick,
    icon,
    circle,
    center,
    siteId,
    zoom,
    kmlUrl,
    vessels,
    shouldReloadKml,
    onReloadKml,
    showBorder = true,
    showMapSettings = true,
    showSiteOverlays = false,
    satelliteView = false,
    onMapReady,
    suppressLegacyLayers = false,
  },
  ref,
) {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [clickedSpot, setClickedSpot] = useState<string>();
  const mapRef = useRef<HTMLDivElement>(null);
  const map = useRef<any>(null);
  const onMapReadyRef = useRef(onMapReady);
  onMapReadyRef.current = onMapReady;
  const uiRef = useRef<H.ui.UI | null>(null);
  const { data: locations = [] } = useLocations({
    enabled: showSiteOverlays || siteId != null,
  });
  const defaultLayersRef = useRef<any>(null);

  const imageOverlays = useMemo((): MapOverlay[] => {
    if (!showSiteOverlays || suppressLegacyLayers) return [];
    return locations
      .filter(
        (loc) =>
          !!loc.mapImage &&
          loc.boundNorth != null &&
          loc.boundSouth != null &&
          loc.boundEast != null &&
          loc.boundWest != null &&
          (siteId == null || loc.id === siteId),
      )
      .map((loc) => ({
        path: loc.mapImage!,
        bounds: {
          north: Number(loc.boundNorth),
          south: Number(loc.boundSouth),
          east: Number(loc.boundEast),
          west: Number(loc.boundWest),
        },
      }));
  }, [locations, showSiteOverlays, siteId, suppressLegacyLayers]);

  const siteCenter = useMemo(() => {
    if (siteId == null) return undefined;
    const loc = locations.find((l) => l.id === siteId);
    if (loc?.latitude == null || loc?.longitude == null) return undefined;
    return { lat: Number(loc.latitude), lng: Number(loc.longitude) };
  }, [locations, siteId]);

  const effectiveCenter = center ?? siteCenter;

  useEffect(() => {
    // Check if the map object has already been created
    if (!map.current && mapRef.current) {
      // Create a platform object with the API key and useCIT option
      const platform = new H.service.Platform({ apikey: HERE_MAP_KEY });
      // Obtain the default map types from the platform object:
      const defaultLayers: any = platform.createDefaultLayers({ pois: true });
      defaultLayersRef.current = defaultLayers;
      // Create a new map instance with the Tile layer, center and zoom level
      // Instantiate (and display) a map:
      const newMap = new H.Map(mapRef.current, defaultLayers.vector.normal.map);

      // add a resize listener to make sure that the map occupies the whole container
      const resize = () => newMap.getViewPort().resize();
      window.addEventListener('resize', resize);
      const observer =
        typeof ResizeObserver === 'undefined'
          ? undefined
          : new ResizeObserver(resize);
      observer?.observe(mapRef.current);

      const mapEvents = new H.mapevents.MapEvents(newMap);
      const behavior = new H.mapevents.Behavior(mapEvents);
      behavior.enable();
      // create the default UI component, for displaying bubbles
      const ui = H.ui.UI.createDefault(newMap, defaultLayers);
      uiRef.current = ui;
      // put map settings control to the top left
      const mapSettingsControl = ui.getControl('mapsettings');
      mapSettingsControl?.setAlignment(H.ui.LayoutAlignment.TOP_LEFT);

      // put zoom rectangle control to the bottom right
      const zoomRectangle = new H.ui.ZoomRectangle({
        alignment: H.ui.LayoutAlignment.RIGHT_BOTTOM,
      });
      ui.addControl('rectangle', zoomRectangle);
      // Set the map object to the reference
      map.current = newMap;
      setMapLoaded(true);
      onMapReadyRef.current?.(newMap);
      return () => {
        onMapReadyRef.current?.(null);
        observer?.disconnect();
        window.removeEventListener('resize', resize);
        behavior.dispose?.();
        mapEvents.dispose?.();
        ui.dispose?.();
        newMap.dispose?.();
        map.current = null;
        uiRef.current = null;
        defaultLayersRef.current = null;
      };
    }
  }, []);

  useEffect(() => {
    uiRef.current?.getControl('mapsettings')?.setVisibility(showMapSettings);
  }, [mapLoaded, showMapSettings]);

  useEffect(() => {
    // Check if the map object has been created
    if (!mapLoaded) return;

    const currentMap: H.Map = map.current;
    // set map center
    currentMap.setCenter(
      effectiveCenter ? toGroundPoint(effectiveCenter) : DEFAULT_MAP_CENTER,
    );
  }, [effectiveCenter, mapLoaded]);

  useEffect(() => {
    // Check if the map object has been created
    if (!mapLoaded) return;

    const currentMap: H.Map = map.current;
    // set map zoom
    currentMap.setZoom(zoom || DEFAULT_MAP_ZOOM);
  }, [mapLoaded, zoom]);

  useEffect(() => {
    if (!mapLoaded || !defaultLayersRef.current || !map.current) return;
    const layers = defaultLayersRef.current;
    const layer = satelliteView
      ? (layers.raster?.satellite?.map ?? layers.vector.normal.map)
      : layers.vector.normal.map;
    map.current.setBaseLayer(layer);
  }, [mapLoaded, satelliteView]);

  useEffect(() => {
    if (!mapLoaded || (!markers && !operatorMarkers)) return;

    const currentMap: H.Map = map.current;
    const objects = currentMap.getObjects();
    objects.forEach((object) => {
      if (object.getData() === 'markers') {
        currentMap.removeObject(object);
      }
    });

    const group = new H.map.Group({ data: 'markers' });

    if (operatorMarkers?.length) {
      operatorMarkers.forEach((marker) => {
        const point = { lat: marker.lat, lng: marker.lng };
        const el = document.createElement('div');
        el.style.width = '16px';
        el.style.height = '16px';
        el.style.borderRadius = '50%';
        el.style.backgroundColor = PRESENCE_COLORS[marker.presence];
        el.style.border =
          marker.linkHealth === 'Degrading'
            ? '3px solid #FF9800'
            : '2px solid #ffffff';
        el.style.boxShadow = '0 1px 4px rgba(0,0,0,0.35)';
        el.title = marker.label ?? `Operator ${marker.operatorId}`;

        const domIcon = new H.map.DomIcon(el, {
          onAttach: () => {},
          onDetach: () => {},
        });
        const domMarker = new H.map.DomMarker(point, {
          icon: domIcon,
          data: 'marker',
        });

        if (onOperatorMarkerClick) {
          domMarker.addEventListener('tap', () =>
            onOperatorMarkerClick(marker.operatorId),
          );
        }

        group.addObject(domMarker);
      });
    } else if (markers) {
      let i = 0;
      markers.forEach((marker) => {
        const markerIcon = new H.map.Icon(icon || `/images/marker.png`, {
          size: { w: 32, h: 32 },
          anchor: { x: 16, y: 16 },
        });

        const point = toGroundPoint(marker);
        const newMarker = new H.map.Marker(point, {
          data: 'marker',
          icon: markerIcon,
        });

        if (vessels) {
          const vessel = vessels[i];
          let statusDescription = '';

          switch (vessel.status) {
            case 0:
              statusDescription = 'Under Way';
              break;
            case 1:
              statusDescription = 'At Anchor';
              break;
            case 2:
              statusDescription = 'Not Under Command';
              break;
            case 3:
              statusDescription = 'Restricted Manoeuvrability';
              break;
            case 4:
              statusDescription = 'Constrained By Her Draught';
              break;
            case 5:
              statusDescription = 'Moored';
              break;
            case 6:
              statusDescription = 'Aground';
              break;
            case 7:
              statusDescription = 'Fishing';
              break;
            default:
              statusDescription = 'Under Way';
          }

          const lastUpdate = new Date(vessel.timestamp).toLocaleString('en-GB');
          let bubble: H.ui.InfoBubble;
          const html = new H.ui.UI(currentMap);
          newMarker.addEventListener(
            'pointerenter',
            () => {
              bubble = new H.ui.InfoBubble(point, {
                content: `<style>
           #bubble{
            font-weight: 650;
            margin-bottom: 0px;
            color: dark grey;
          }
          .nowrap {
          white-space: nowrap;
          }
          .H_ib_body {
          opacity: 0.8;
          }
          </style>
          
          <body><div id="bubble"><span class="nowrap">${vessel.shipName}</span></div><div id="bubble"><span class="nowrap"> Status: ${statusDescription}</span></div> <div id="bubble"> Speed: ${vessel.speed} kn</div> <div id="bubble"><span class="nowrap"> Course: ${vessel.course}°</span></div> <div id="bubble"> <span class="nowrap"> At: ${lastUpdate} </div> </body>`,
              });
              html.addBubble(bubble);
            },
            false,
          );
          newMarker.addEventListener(
            'pointerleave',
            () => {
              setTimeout(() => {
                bubble.close();
              }, 400);
            },
            false,
          );
        }
        group.addObject(newMarker);
        i += 1;
      });
    }

    currentMap.addObject(group);
  }, [
    icon,
    mapLoaded,
    markers,
    onOperatorMarkerClick,
    operatorMarkers,
    vessels,
  ]);

  useEffect(() => {
    // Check if the map object has been created
    if (!mapLoaded || !circle) return;

    const currentMap: H.Map = map.current;
    // Remove all circles from the map
    const objects = currentMap.getObjects();
    objects.forEach((object) => {
      if (object.getData() === 'circles') {
        currentMap.removeObject(object);
      }
    });

    // Create a group object to hold the circles
    const group = new H.map.Group({ data: 'circles' });

    // Create a new circle with the given center and radius
    group.addObject(
      new H.map.Circle(
        // The central point of the circle
        toGroundPoint(circle.center),
        // The radius of the circle in meters
        circle.radius || 1000,
        {
          style: {
            strokeColor: 'rgba(55, 85, 170, 0.6)', // Color of the perimeter
            lineWidth: 0,
            fillColor: 'rgba(55, 85, 170, 0.6)', // Color of the circle
          },
        } as any, // Type assertion to avoid type errors
      ),
    );

    // Add the group of circles to the map
    currentMap.addObject(group);
  }, [circle, mapLoaded]);

  useEffect(() => {
    if (!mapLoaded || !kmlUrl || suppressLegacyLayers) return;
    const currentMap: H.Map = map.current;
    const origin = process.env.VITE_APP_BASE_API_URL?.replace('/api', '');
    const reader = new H.data.kml.Reader(`${origin}${kmlUrl}`);
    let active = true;
    let layer: H.map.layer.ObjectLayer | undefined;
    const onTap = (event: any) => setClickedSpot(event.target.getData()?.name);
    const onState = (event: any) => {
      if (!active || event.state !== H.data.AbstractReader.State.READY) return;
      layer = reader.getLayer();
      currentMap.addLayer(layer);
      layer.getProvider().addEventListener('tap', onTap);
      onReloadKml?.();
    };
    reader.addEventListener('statechange', onState);
    reader.parse();
    return () => {
      active = false;
      reader.removeEventListener('statechange', onState);
      if (layer && map.current === currentMap) {
        layer.getProvider().removeEventListener('tap', onTap);
        currentMap.removeLayer(layer);
      }
      setClickedSpot(undefined);
    };
  }, [mapLoaded, kmlUrl, onReloadKml, shouldReloadKml, suppressLegacyLayers]);

  useImperativeHandle(ref, () => ({
    takeSnapshot: (): Promise<string | null> =>
      new Promise((resolve) => {
        const currentMap: H.Map = map.current;
        if (!currentMap) {
          resolve(null);
          return;
        }
        currentMap.capture((canvas: HTMLCanvasElement | null) => {
          if (canvas) {
            try {
              resolve(canvas.toDataURL('image/png'));
            } catch {
              resolve(null);
            }
          } else {
            console.error('Capturing map failed.');
            resolve(null);
          }
        });
      }),
    getView: (): MapFullscreenData | null => {
      const currentMap: H.Map = map.current;
      if (currentMap) {
        const zoom = currentMap.getZoom();
        const bounds = currentMap
          .getViewModel()
          .getLookAtData()
          .bounds?.getBoundingBox();
        if (bounds) {
          return { zoom, bounds };
        }
      }
      return null;
    },
  }));

  // Function to add image overlay to the map
  const addImageOverlay = useCallback((items: MapOverlay[]) => {
    const currentMap: H.Map = map.current;
    if (!currentMap) return;

    // Remove existing image overlay if any
    const objects = currentMap.getObjects();
    objects.forEach((object) => {
      if (object.getData() === 'images') {
        currentMap.removeObject(object);
      }
    });

    // Create a group object to hold the image overlays
    const group = new H.map.Group({ data: 'images' });
    items.forEach((item) => {
      // Create a bounding box for the image
      const boundingBox = new H.geo.Rect(
        item.bounds.north,
        item.bounds.west,
        item.bounds.south,
        item.bounds.east,
      );

      // Create a new image overlay
      const overlay = new H.map.Overlay(
        boundingBox,
        item.path,
        // H.map.Overlay doesn't accept options like opacity in this API version
      );

      group.addObject(overlay);
    });
    // Add the overlay to the map
    currentMap.addObject(group);
  }, []);

  // Image overlays from locations that have mapImage + bounds
  useEffect(() => {
    if (!mapLoaded) return;
    addImageOverlay(imageOverlays);
  }, [mapLoaded, addImageOverlay, imageOverlays]);

  // Return a div element to hold the map
  return (
    <>
      <div
        className={`h-100 w-100${showBorder ? ' border rounded' : ''}`}
        ref={mapRef}
      />
      {clickedSpot ? <div>{clickedSpot}</div> : null}
    </>
  );
});
